import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {HostingService} from "../hosting.service";
import {DbResponse} from "../../../../shared/interceptors/db-response";
import {Website} from "../../website";
import {DialogService} from "../../../../../ng2-bootstrap-modal/dialog.service";
import {PushToLiveConfirmationComponent} from "./push-to-live/push-to-live-confirmation.component";
import {NotifierComponent} from "../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../shared/components/notifier/notification-type";
import * as fromStore from '../../../../store';
import {delay, filter, first, switchMap, takeUntil} from 'rxjs/operators';
import {combineLatest} from 'rxjs';
import {Store} from '@ngrx/store';
import {ConfirmDialogComponent} from '../../../../shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import {TextUtils} from '../../../../shared/utils/text-utils';
import {Config} from "../../../../config/config";
import {ActivityStatus, IActivity} from "../../../../activities/activity";
import {Observable} from "rxjs";
import {Subject} from "rxjs";
import {WebsiteService} from "../../../website.service";
import {User} from "../../../../user/user";
import {GoogleAnalyticsService} from "../../../../shared/services/google-analytics/google-analytics.service";
import {UserSubscription} from "../../../../user/user-subscription";
import {Utils} from "../../../../shared/utils/utils";
import {DbModalService} from "../../../../modules/modal/modal";
import {TypeUtils} from "../../../../shared/utils/type-utils";
import {
  OneWeekFreeDemoUpgradeComponent
} from "../../../../shared/components/dialogs/one-month-free/components/one-week-free-demo-upgrade/one-week-free-demo-upgrade.component";
import {
  PricingPageViewComponent
} from "../../../../shared/components/dialogs/one-month-free/components/pricing-page-view/pricing-page-view.component";

@Component({
    selector: 'db-hosting-manage-environment',
    templateUrl: './hosting-manage-environment.component.html',
    styleUrls: ['./hosting-manage-environment.component.scss'],
    host: { '[class.ds-loading]': 'loading' },
    standalone: false
})
export class HostingManageEnvironmentComponent implements OnInit, OnDestroy {

  public website: Website;
  public currentSubscription: UserSubscription;

  public syncingWithLive: boolean;
  public loading: boolean;
  public confirmationSubdomain: any;
  public configHostname: string = Config.hostname;
  public stagingAccessOnly: boolean;
  public stagingEnabled: boolean;
  public ongoingActivityDisable$: Observable<IActivity>;
  public ongoingActivityEnable$: Observable<IActivity>;
  public ongoingActivityPushToStaging$: Observable<IActivity>;
  public features: any = [];
  public demoUser: boolean = false;

  private _user: User;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _store: Store<fromStore.DbStoreState>,
    private _hostingService: HostingService,
    private _websiteService: WebsiteService,
    private _dialogService: DialogService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _modalService: DbModalService,
  ) {}

  ngOnInit(): void {
    this.features = [
      TextUtils.getText('text_informativeBannerStagingFeature1'),
      TextUtils.getText('text_informativeBannerStagingFeature2'),
      TextUtils.getText('text_informativeBannerStagingFeature3')
    ];
    if (this._route && this._route.snapshot && this._route.snapshot.parent && this._route.snapshot.parent.parent) {
      this.website = this._route.snapshot.parent.parent.data['website'];
      this._user = this._route.snapshot.parent.parent.data['user'];
      this.currentSubscription = this._route.snapshot.parent.parent.data['subscription'];
      this.stagingEnabled = this._route.snapshot.parent.parent.data['websiteSettings']
        && this._route.snapshot.parent.parent.data['websiteSettings'].staging_enabled;
    }
    this._store.select(fromStore.getHostingSettingsLoaded(this.website && this.website.id))
      .pipe(
        filter((loaded: boolean) => !!loaded),
        switchMap(() => this._store.select(fromStore.getHostingSettings(this.website && this.website.id))),
        first()
      )
      .subscribe((response: any) => {
        this.stagingEnabled = Utils.isTruthy(response.staging_enabled);
      });
    this.confirmationSubdomain = this.website && this.website.name.replace("https://", "").split("." + this.configHostname)[0].replace("-dev", "");
    this.getHostName();
    this.ongoingActivityEnable$ = this._store.select(fromStore.getEnableDevEnvironmentOngoingActivity(this.website.id));
    this.ongoingActivityDisable$ = this._store.select(fromStore.getDisableDevEnvironmentOngoingActivity);
    this.ongoingActivityPushToStaging$ = this._store.select(fromStore.gePushToStagingOngoingActivity(this.website.id));
    this.ongoingActivityEnable$
      .pipe(
        takeUntil(this._destroyStream$),
      )
      .subscribe((activity: IActivity): void => {
        if (activity && activity.status === ActivityStatus.DONE) {
          this.loading = false;
          this._store.dispatch(new fromStore.LoadHostingSettings(this.website?.isNetwork && !this.website?.parentId && this.website?.children && this.website?.children.length ? this.website?.children[0] : this.website?.id));
          this._store.select(fromStore.getWebsite(activity.meta.id))
            .pipe(
              filter(website => !!website),
              first()
            )
            .subscribe(website => {
              window.location.href = window.location.origin + '/websites/' + website.id + '/main';
            });
        }
      });
    this.ongoingActivityDisable$
      .pipe(
        takeUntil(this._destroyStream$),
      )
      .subscribe((activity: IActivity): void => {
        if (activity && activity.status === ActivityStatus.DONE) {
          this._store.dispatch(new fromStore.RemoveWebsite(this.website.siblingId.dev));
          this._store.dispatch(new fromStore.LoadHostingSettings(this.website.isNetwork && !this.website.parentId && this.website.children && this.website.children.length ? this.website.children[0].siblingId.live : this.website.siblingId.live));
          if (this._router.url.includes(`websites/${this.website.id}/`)) {
            this._router.navigate(['/websites/' + this.website.siblingId.live]);
          }
        }
      });

    this.ongoingActivityPushToStaging$
      .pipe(
        takeUntil(this._destroyStream$),
      )
      .subscribe((activity: IActivity): void => {
        if (activity && activity.status === ActivityStatus.DONE) {
          this.syncingWithLive = false;
          this._store.dispatch(new fromStore.UpdateWebsiteData(this.website.siblingId.dev));
          this._hostingService.clearCache( this.website.siblingId.dev );
          NotifierComponent.notify(new Notification('Successfully pushed to staging', NotificationType.SUCCESS));
        }
      });
    this._store.select(fromStore.getWebsitesLoaded).pipe(
      filter((loaded: boolean) => loaded),
      switchMap(() => this._store.select(fromStore.getWebsites)),
      first()
    ).subscribe((websites: Website[]) => {
      this.stagingAccessOnly = this.website.type === 'staging' ? !websites.find((website) => website.id === this.website.siblingId.live) : false;
    });
    this._getUserAndSubscriptionData();

  }

  private _getUserAndSubscriptionData(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserSubscription)
        )),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([user, currentSubscription]) => {
        this._user = user;
        this.currentSubscription = currentSubscription;
        this.demoUser = Utils.isDemoUser(currentSubscription, user);
      });
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  public getHostName(): void {
    this._hostingService.getUserMainDomain()
      .pipe(filter((response: DbResponse) => !!response),
      first())
      .subscribe((response: DbResponse) => {
        if (response.success) {
          this.configHostname = response.data.domain;
          this.confirmationSubdomain = this.website.name.replace("https://", "").split("." + this.configHostname)[0].replace("-dev", "");
        }
      });
  }

  public pushToLive(): void {
    !this.stagingAccessOnly && (this.stagingEnabled || this.website.type === 'staging') && this._dialogService
      .addDialog(PushToLiveConfirmationComponent, {website: this.website});
  }

  public syncWithLive() {
    !this.stagingAccessOnly && (this.stagingEnabled || this.website.type === 'staging') && this._dialogService
      .addDialog(ConfirmDialogComponent, {
        header: TextUtils.getText('text_hostingSyncWithLiveDialogHeader'),
        message: TextUtils.getText('text_hostingSyncWithLiveDialogMessage'),
        cancelButtonText: TextUtils.getText('text_cancel'),
        confirmButtonText: TextUtils.getText('test_syncToLive'),
        website: this.website,
        checkSubdomain: true,
        confirmButtonClass: 'ds-btn-blue'
      })
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.syncingWithLive = true;

          this._hostingService.syncWithLive(this.website.siblingId.live)
            .pipe(
              filter((response: DbResponse): boolean => !!response),
              first(),
              delay(1000)
            )
            .subscribe((response: DbResponse): void => {
              if (!response.success) {
                response.message && NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
              }
            });
        }
      });
  }

  public enableStagingEnvironment(): void {
    if (this.currentSubscription?.trialClient) {
        this._router.navigate(['/upgrade-plan']);
        return;
    }

    this.loading = true;

    this._websiteService.enableWebsiteDevEnvironment(this.website.id)
      .pipe(
        filter((response: DbResponse): boolean => !!response),
        first()
      )
      .subscribe((response: DbResponse): void => {
        if (!response.success) {
          NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
        }
      });
  }

  public disableStagingEnvironment(): void {
    this._dialogService
      .addDialog(ConfirmDialogComponent, {
        header: TextUtils.getText('hostingManageEnvironment.disableHeader'),
        message: TextUtils.getText('hostingMangeEnvironment.disableDialogMessage'),
        confirmButtonText: TextUtils.getText('text_proceed'),
        checkSubdomain: true,
        confirmButtonClass: 'ds-btn-white-gray-border',
        alignLeft: true,
        fromStaging: true,
        width: '700px'
      })
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.loading = true;

          this._hostingService.disableStagingEnvironment(this.website.siblingId.dev)
            .pipe(
              filter((response: DbResponse): boolean => !!response),
              first()
            )
            .subscribe((response: DbResponse): void => {
              if (!response.success) {
                response.message && NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
              }
            });
        }
      });
  }

  onMouseMove($event: any) {
    const x = $event.clientX, y = $event.clientY;
    if ($event.target && $event.target.children && $event.target.children[1]) {
      const recordItemNameTooltipSpan = $event.target.children[1];
      recordItemNameTooltipSpan.style.top = (y + 20) + 'px';
      recordItemNameTooltipSpan.style.left = (x - 250) + 'px';
    }
  }
}
